import { z } from "zod";
import { assetTreeSearchSchema } from "./assetTreeSearch";
import { parseRequestsPageView } from "./requestsPageView";
import { workOrdersSearchSchema } from "./workOrdersSearch";

/**
 * What each module retains for the session. A codec lists the keys that
 * belong to a module's list view, validates a candidate object down to those
 * keys (dropping anything invalid silently, so stale stored or URL values never
 * break a list), and says whether the result carries any active state. Adding
 * a retained key later, such as a sort, means adding it to `keys` and `pick`.
 */

export type RetainedListModule =
  | "assets"
  | "work-orders"
  | "requests"
  | "procedures"
  | "teams-users";

export type RetainedListValues = Record<string, unknown>;

export type RetainedListCodec = {
  keys: readonly string[];
  pick: (search: Record<string, unknown> | undefined) => RetainedListValues;
  isEmpty: (picked: RetainedListValues) => boolean;
};

function hasValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function keepKeys(
  keys: readonly string[],
  source: Record<string, unknown> | undefined,
): RetainedListValues {
  const result: RetainedListValues = {};
  if (!source) return result;
  for (const key of keys) {
    if (hasValue(source[key])) result[key] = source[key];
  }
  return result;
}

function isEmptyWith(
  keys: readonly string[],
  defaults: Record<string, unknown> = {},
) {
  return (picked: RetainedListValues) =>
    !keys.some((key) => hasValue(picked[key]) && picked[key] !== defaults[key]);
}

const ASSET_KEYS = [
  "assetSearch",
  "assetKind",
  "assetSort",
  "assetSortDir",
  "assetSelectedId",
  "assetCollapsedIds",
] as const;

const WORK_ORDER_KEYS = [
  "workOrderView",
  "workOrderSearch",
  "workOrderStatuses",
  "workOrderPriorities",
  "workOrderTypes",
  "workOrderAssetIds",
  "workOrderCategoryIds",
  "workOrderAssignees",
  "workOrderDueDateFrom",
  "workOrderDueDateTo",
] as const;

const REQUEST_KEYS = [
  "view",
  "search",
  "asset",
  "priority",
  "status",
  "requester",
  "submittedFrom",
  "submittedTo",
] as const;

const PROCEDURE_KEYS = ["search", "categories", "owners"] as const;

const TEAMS_USERS_KEYS = [
  "tab",
  "search",
  "roles",
  "teamStatuses",
  "userSort",
  "userSortDir",
  "teamSort",
  "teamSortDir",
] as const;

const textSchema = z.string().max(500);
const listSchema = z.array(z.string().trim().min(1).max(120)).max(100);
const teamStatusSchema = z.enum(["active", "inactive"]);

type Parser = {
  safeParse: (
    value: unknown,
  ) => { success: true; data: Record<string, unknown> } | { success: false };
};

function parseWith(
  schema: Parser,
  keys: readonly string[],
  search: Record<string, unknown> | undefined,
): RetainedListValues {
  const parsed = schema.safeParse(keepKeys(keys, search));
  return parsed.success ? keepKeys(keys, parsed.data) : {};
}

const assetsCodec: RetainedListCodec = {
  keys: ASSET_KEYS,
  pick: (search) => parseWith(assetTreeSearchSchema, ASSET_KEYS, search),
  isEmpty: isEmptyWith(ASSET_KEYS),
};

const workOrdersCodec: RetainedListCodec = {
  keys: WORK_ORDER_KEYS,
  pick: (search) => parseWith(workOrdersSearchSchema, WORK_ORDER_KEYS, search),
  isEmpty: isEmptyWith(WORK_ORDER_KEYS, { workOrderView: "mine" }),
};

const requestsCodec: RetainedListCodec = {
  keys: REQUEST_KEYS,
  pick: (search) =>
    keepKeys(
      REQUEST_KEYS,
      parseRequestsPageView(keepKeys(REQUEST_KEYS, search)),
    ),
  isEmpty: isEmptyWith(REQUEST_KEYS, { view: "all" }),
};

const proceduresCodec: RetainedListCodec = {
  keys: PROCEDURE_KEYS,
  pick: (search) =>
    parseWith(
      z.object({
        search: textSchema.optional().catch(undefined),
        categories: listSchema.optional().catch(undefined),
        owners: listSchema.optional().catch(undefined),
      }),
      PROCEDURE_KEYS,
      search,
    ),
  isEmpty: isEmptyWith(PROCEDURE_KEYS),
};

const teamsUsersCodec: RetainedListCodec = {
  keys: TEAMS_USERS_KEYS,
  pick: (search) =>
    parseWith(
      z.object({
        tab: z.enum(["users", "teams"]).optional().catch(undefined),
        search: textSchema.optional().catch(undefined),
        roles: listSchema.optional().catch(undefined),
        // Each tab keeps its own sort; an unapproved field or direction is
        // dropped so the tab falls back to its default order.
        userSort: z
          .enum(["name", "role", "teams", "lastLogin"])
          .optional()
          .catch(undefined),
        userSortDir: z.enum(["asc", "desc"]).optional().catch(undefined),
        teamSort: z
          .enum(["team", "lead", "members", "description"])
          .optional()
          .catch(undefined),
        teamSortDir: z.enum(["asc", "desc"]).optional().catch(undefined),
        teamStatuses: z
          .array(z.unknown())
          .transform((values) =>
            values.filter(
              (value): value is z.infer<typeof teamStatusSchema> =>
                teamStatusSchema.safeParse(value).success,
            ),
          )
          .optional()
          .catch(undefined),
      }),
      TEAMS_USERS_KEYS,
      search,
    ),
  isEmpty: isEmptyWith(TEAMS_USERS_KEYS, { tab: "users" }),
};

export const retainedListCodecs: Record<RetainedListModule, RetainedListCodec> =
  {
    assets: assetsCodec,
    "work-orders": workOrdersCodec,
    requests: requestsCodec,
    procedures: proceduresCodec,
    "teams-users": teamsUsersCodec,
  };
